/**
 * Alertas de asignación — el ÚNICO sitio que conoce la forma del backend.
 *
 * Las vistas y el store hablan con estas funciones y reciben siempre la forma
 * de la vista:
 *
 *   { id, tipo: "transfer" | "assign", de: {id, nombre}, para: {id, nombre},
 *     grupo, fecha (ISO con zona),
 *     items: [{ id, itemId, alertaId, contacto, empresa,
 *               estado: "nueva" | "vista" | "resuelta",
 *               motivo: "repartida" | "reasignada" | "retirada" | null }] }
 *
 * `items[].id` es el id con el que la VISTA encuentra la fila o tarjeta
 * (interacción en la Bandeja, gestión en las tarjetas); `itemId`, el del ítem
 * de alerta, que es el que va a `mark_seen/`.
 *
 * Contrato REAL: `api/fxCrm/CrmAssignmentAlert/`
 * (`.claude/alertas_crm/CRM-bandeja-y-alertas-front.md` §8; análisis en
 * `analisis-contrato-backend.md`). Los datos de ejemplo de las fases sin
 * backend se retiraron el 2026-10-04; queda el simulador de desarrollo (abajo).
 */
import { apiClient } from "@/api/softphone_api";
import { reassignScope } from "@/modules/fxCrm/helpers/reassignTargets";

const URL_ALERTAS = "api/fxCrm/CrmAssignmentAlert/";
/**
 * Alertas por pedido. El listado viene paginado (10 por defecto) y de la más
 * nueva a la más vieja; la campana pinta 8. Con 50 quedan cubiertas las
 * transferencias sin ver que resaltan filas de la Bandeja (las tarjetas del
 * gestor traen su propio `assignment_alert`, no dependen de esto).
 */
const LIMITE = 50;

/** Quién mira: su id, su rol para las alertas y cómo se llama. */
export function quienMira(userInfo) {
  const rol = reassignScope(userInfo) || "gestor";
  // `Display` NO es el nombre: es la extensión (el perfil la pinta como
  // «Extensión #Display»). El nombre es `ShortName`.
  const nombre =
    userInfo?.ShortName ||
    [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(" ") ||
    userInfo?.Username ||
    "Tú";
  return { userId: userInfo?.User_id ?? null, rol, yo: { id: userInfo?.User_id ?? null, nombre } };
}

// ---------------------------------------------------------------------------
// Traducción backend → vista (contrato real, 2026-10-02)
// ---------------------------------------------------------------------------

const ESTADO = { unseen: "nueva", seen: "vista", resolved: "resuelta" };
const MOTIVO = { distributed: "repartida", reassigned: "reasignada", withdrawn: "retirada" };

const persona = (user) =>
  user
    ? {
        id: user.id,
        nombre:
          user.full_name ||
          [user.first_name, user.last_name].filter(Boolean).join(" ") ||
          "Sin nombre",
      }
    : null;

/**
 * El backend manda las fechas SIN zona, en hora de Bogotá (contrato,
 * «Convenciones»). `new Date()` las leería como hora del navegador: se les
 * pone `-05:00` para que «hace X» sea correcto en cualquier zona.
 */
export function fechaBogota(iso) {
  if (!iso || /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso)) return iso;
  return `${iso}-05:00`;
}

/**
 * Una alerta del backend → la forma de la vista.
 *
 * `item.id` es el id con el que la VISTA encuentra la fila o tarjeta, y
 * depende del tipo (análisis §1):
 *   · transferencia → `crm_interaction` (las filas de la Bandeja son
 *     interacciones; `itx_management` viene null);
 *   · asignación    → `itx_management` (las tarjetas son gestiones).
 * `item.itemId` es el id del ítem de alerta: el que va a `mark_seen/`.
 */
function traducirAlerta(fila) {
  const esTransferencia = fila.kind === "transfer";
  return {
    id: fila.id,
    tipo: esTransferencia ? "transfer" : "assign",
    de: persona(fila.sender),
    para: persona(fila.recipient),
    grupo: fila.crm_group?.name || null,
    fecha: fechaBogota(fila.created_at),
    items: (fila.items || []).map((item) => ({
      id: esTransferencia
        ? item.crm_interaction
        : item.itx_management ?? item.crm_interaction,
      itemId: item.id,
      alertaId: fila.id,
      contacto: item.contact_name || "Sin nombre",
      empresa: item.company_name || null,
      estado: ESTADO[item.status] || "nueva",
      motivo: MOTIVO[item.resolution] || null,
    })),
  };
}

/**
 * La marca que trae CADA tarjeta y fila del gestor (contrato §8, «Aviso en la
 * tarjeta»), en `ItxSimpleManagement/` y en `by-status/`:
 *
 *   "assignment_alert": { item_id, alert_id, sender: {…}|null, created_at } | null
 *
 * El backend solo la manda si quien mira es el destinatario, el ítem sigue sin
 * ver y la tarjeta sigue a su nombre. Es la fuente fiable de la franja: no
 * depende de qué alertas cargó la campana. Devuelve un ítem con la forma de la
 * vista (con `de` y `fecha`, porque su alerta puede no estar en el store), o
 * `null`.
 */
export function traducirAlertaDeFila(fila) {
  const marca = fila?.assignment_alert;
  if (!marca) return null;
  return {
    id: fila.id,
    itemId: marca.item_id,
    alertaId: marca.alert_id,
    contacto: "",
    empresa: null,
    estado: "nueva",
    motivo: null,
    tipo: "assign",
    de: persona(marca.sender),
    fecha: fechaBogota(marca.created_at),
  };
}

/** Una alerta llegada por socket (`datos.alert`), ya en la forma de la vista. */
export function traducirAlertaSocket(datos) {
  return traducirAlerta(datos?.alert || datos);
}

const filas = (res) => (Array.isArray(res?.data) ? res.data : res?.data?.results || []);

// ---------------------------------------------------------------------------
// Lectura
// ---------------------------------------------------------------------------

/**
 * Las alertas de quien mira: las que RECIBIÓ (campana, resaltados), las que
 * ENVIÓ (seguimiento de task_4; solo admin y coordinador) y el contador de la
 * campana tal como lo cuenta el servidor (`summary/`).
 *
 * @returns {Promise<{ recibidas: Array, enviadas: Array, unseenCount: number|null }>}
 */
export async function fetchAlerts(userInfo) {
  const { rol } = quienMira(userInfo);
  const pedirEnviadas = rol !== "gestor";
  const [recibidas, enviadas, resumen] = await Promise.all([
    apiClient.get(URL_ALERTAS, { params: { box: "received", limit: LIMITE } }),
    pedirEnviadas
      ? apiClient.get(URL_ALERTAS, { params: { box: "sent", limit: LIMITE } })
      : Promise.resolve(null),
    apiClient.get(`${URL_ALERTAS}summary/`),
  ]);
  return {
    recibidas: filas(recibidas).map(traducirAlerta),
    enviadas: filas(enviadas).map(traducirAlerta),
    unseenCount: resumen?.data?.unseen_items ?? null,
  };
}


/**
 * ¿En qué línea de negocio está esta gestión? Para saber adónde llevar al
 * hacer clic en una alerta de ASIGNACIÓN: el socket no dice si la gestión
 * sigue en Gestión Interacciones (sin línea) o ya está en el funnel (por
 * ejemplo, reasignada desde el detalle). Lo dice su detalle,
 * `GET InteractionManagement/{id}/` (el mismo que usa el panel de detalle).
 *
 * @returns {Promise<{ id: number, name: string } | null>} la línea, o `null` si no tiene.
 *   Lanza si la consulta falla (quien llama cae al destino de siempre).
 */
export async function lineaDeGestion(gestionId) {
  const { data } = await apiClient.get(`api/fxCrm/InteractionManagement/${gestionId}/`);
  const linea = data?.business_line;
  if (!linea) return null;
  return typeof linea === "object" ? { id: linea.id, name: linea.name || "" } : { id: linea, name: "" };
}

// ---------------------------------------------------------------------------
// Escritura. Un solo endpoint: `POST mark_seen/`. No capturan el error: el
// store revierte y decide cómo avisar. Devuelven `{ contador, managements }`:
// el contador nuevo (`unseen_items`) y las tarjetas que perdieron el aviso
// (`managements`, ids de gestión), o `null` si no se llamó.
// ---------------------------------------------------------------------------

async function marcar(cuerpo) {
  const { data } = await apiClient.post(`${URL_ALERTAS}mark_seen/`, cuerpo);
  return {
    contador: Number.isInteger(data?.unseen_items) ? data.unseen_items : null,
    managements: Array.isArray(data?.managements) ? data.managements : [],
  };
}

/**
 * Marca vistos estos ítems de alerta (`itemId`). Es lo que usan «Enterado»,
 * «Marcar como vista» y abrir la tarjeta, en transferencias y asignaciones por
 * igual (las transferencias no tienen `managements`).
 */
export async function markItemsSeen(userInfo, itemIds) {
  // Solo los ítems del backend (numéricos): los de una alerta simulada son
  // inventados y no se mandan. Si no queda ninguno, no se llama.
  const reales = itemIds.filter((id) => Number.isInteger(id));
  if (!reales.length) return null;
  return marcar({ items: reales });
}

/** Marca vista una alerta entera. */
export async function markAlertSeen(userInfo, alertaId) {
  if (!Number.isInteger(alertaId)) return null; // simulada: no existe en el backend
  return marcar({ alerts: [alertaId] });
}

/** «Marcar todo como visto»: `{}` (y solo `{}`) marca todo. */
export async function markAllSeen() {
  return marcar({});
}

// ---------------------------------------------------------------------------
// Solo desarrollo: `window.__fxSimularAlerta()` (FxCrmLayout)
// ---------------------------------------------------------------------------

let secuenciaSimulada = 0;

/**
 * Crea una alerta RECIBIDA de mentira, como si acabara de llegar por socket.
 * Vive solo en memoria (no se manda al backend): sirve para ver franjas,
 * chips, el aviso del coordinador y los avisos emergentes sin generar una
 * asignación real. Con `ids` reales de la vista se ve el resaltado.
 *
 * @param {{ ids?: number[], tipo?: "transfer"|"assign", de?: string, grupo?: string, contactos?: string[] }} opciones
 */
export function simularAlerta(userInfo, opciones = {}) {
  const { rol, yo } = quienMira(userInfo);
  const tipo = opciones.tipo || (rol === "coordinator" ? "transfer" : "assign");
  const ids = opciones.ids?.length ? opciones.ids : [Math.floor(910000 + Math.random() * 9999)];
  const contactos = opciones.contactos || [];
  secuenciaSimulada += 1;
  const id = `sim-${Date.now().toString(36)}-${secuenciaSimulada}`;

  return {
    id,
    tipo,
    de: { id: 0, nombre: opciones.de || (tipo === "transfer" ? "Administración" : "Andrea Torres") },
    para: yo,
    grupo: opciones.grupo ?? "Equipo Andrea",
    fecha: new Date().toISOString(),
    items: ids.map((itemId, i) => ({
      id: itemId,
      // Inventado a propósito (no numérico): `markItemsSeen` no lo manda.
      itemId: `${id}:${itemId}`,
      alertaId: id,
      contacto: contactos[i] || `Contacto #${itemId}`,
      empresa: null,
      estado: "nueva",
      motivo: null,
    })),
  };
}
